import { setClient } from 'glimmer-apollo'
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core'
import config from 'customer-flows/config/environment'
import { setContext } from '@apollo/client/link/context'
import uid from 'customer-flows/utils/uid'
import type ApplicationInstance from '@ember/application/instance'
import { lookupStore } from 'customer-flows/utils/core'
import type AccertifyService from 'customer-flows/services/accertify'

interface Context {
  headers?: {
    authorizationOverride?: string
    sessionIdOverride?: string
  }
}

export default function setupApolloClient (applicationInstance: ApplicationInstance): void {
  const session = applicationInstance?.lookup('service:session') as Session
  const accertify = applicationInstance?.lookup('service:accertify') as AccertifyService
  const programStore = lookupStore(applicationInstance, 'program/booking-flow')

  const httpLink = createHttpLink({
    uri: `${config.apollo.apiURL}`,
    fetch: customFetch
  })

  const authLink = setContext( (_request, context: Context) => {
    const sessionData = session.restore()

    const headers = {
      Authorization: context?.headers?.authorizationOverride ?? `Key ${session.apiKey}`,
      'Session-ID': context?.headers?.sessionIdOverride ?? sessionData['id'],
      'X-Request-ID': uid(false),
      'X-Booking-Cutoff': programStore?.ignoreBookingCutoff,
      ...(accertify.deviceTID ? { 'X-DeviceTID': accertify.deviceTID } : {}),
      // merge any additional headers from context
      ...context?.headers
    }

    return { ...context, headers }
  })

  const cache = new InMemoryCache()

  const apolloClient = new ApolloClient({
    link: authLink.concat(httpLink),
    cache
  })

  setClient(applicationInstance, apolloClient)
}

export const customFetch = async (uri: RequestInfo | URL, options: RequestInit | undefined): Promise<Response> => {
  const { operationName }: { operationName: string } = JSON.parse(options?.body as string)
  return await fetch(`${uri}/${operationName}`, options)
}
